import { useState } from "react";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import {
  Button,
  Checkbox,
  Modal,
  TextArea,
  TextInput,
} from "@bfc-software/pallet";
import type { DeliverSchemaTypes } from "@bfc-software/api-schemas";

export type Question = DeliverSchemaTypes.components["schemas"]["DashQuestion"];
export type QuestionInput =
  DeliverSchemaTypes.components["schemas"]["DashQuestionInput"];

/**
 * The form holds every field as text, so a half-typed number is a draft rather
 * than a coerced `NaN`, and only becomes a `QuestionInput` once it all parses.
 */
type Draft = {
  questionCode: string;
  questionSeq: string;
  question: string;
  questionLevel: string;
  questionType: string;
  frequency: string;
  answerType: string;
  validationTable: string;
  pageNumber: string;
  pageSeq: string;
  printOnTraxInv: string;
  parentReactiveFlag: string;
  entryRequired: boolean;
  sampleQuestion: boolean;
  activeFlag: boolean;
};

const EMPTY_DRAFT: Draft = {
  questionCode: "",
  questionSeq: "",
  question: "",
  questionLevel: "",
  questionType: "",
  frequency: "",
  answerType: "",
  validationTable: "",
  pageNumber: "",
  pageSeq: "",
  printOnTraxInv: "",
  parentReactiveFlag: "",
  entryRequired: false,
  sampleQuestion: false,
  activeFlag: true,
};

function draftOf(question: Question | null): Draft {
  if (!question) return EMPTY_DRAFT;
  return {
    questionCode: question.questionCode,
    questionSeq: String(question.questionSeq),
    question: question.question,
    questionLevel: question.questionLevel,
    questionType: question.questionType,
    frequency: question.frequency,
    answerType: question.answerType,
    validationTable: question.validationTable,
    pageNumber: String(question.pageNumber),
    pageSeq: String(question.pageSeq),
    printOnTraxInv: question.printOnTraxInv,
    parentReactiveFlag: question.parentReactiveFlag,
    entryRequired: question.entryRequired,
    sampleQuestion: question.sampleQuestion,
    activeFlag: question.activeFlag,
  };
}

const isWhole = (value: string) => /^-?\d+$/.test(value.trim());

/** The request body, or null while a required field is still missing or malformed. */
function toInput(draft: Draft): QuestionInput | null {
  const required = [
    draft.questionCode,
    draft.question,
    draft.questionLevel,
    draft.questionType,
    draft.frequency,
    draft.answerType,
  ];
  if (required.some((value) => !value.trim())) return null;
  if (![draft.questionSeq, draft.pageNumber, draft.pageSeq].every(isWhole)) {
    return null;
  }

  return {
    questionCode: draft.questionCode.trim(),
    questionSeq: Number(draft.questionSeq),
    question: draft.question.trim(),
    questionLevel: draft.questionLevel.trim(),
    questionType: draft.questionType.trim(),
    frequency: draft.frequency.trim(),
    answerType: draft.answerType.trim(),
    validationTable: draft.validationTable.trim(),
    pageNumber: Number(draft.pageNumber),
    pageSeq: Number(draft.pageSeq),
    printOnTraxInv: draft.printOnTraxInv.trim(),
    parentReactiveFlag: draft.parentReactiveFlag.trim(),
    entryRequired: draft.entryRequired,
    sampleQuestion: draft.sampleQuestion,
    activeFlag: draft.activeFlag,
  };
}

/**
 * Add or edit a question. `question` is the row being edited, or null to add.
 * Seeded from the row each time the modal opens: it is keyed on the row id, so
 * one row's draft is never carried into the next.
 */
export function QuestionModal({
  isOpen,
  question,
  onOpenChange,
  onSubmit,
  isPending,
  t,
}: {
  isOpen: boolean;
  question: Question | null;
  onOpenChange: (open: boolean) => void;
  /** Rejects when the save failed; the caller has already reported it. */
  onSubmit: (input: QuestionInput) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  return (
    <QuestionForm
      key={`${isOpen}-${question?.id ?? "new"}`}
      isOpen={isOpen}
      question={question}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      isPending={isPending}
      t={t}
    />
  );
}

function QuestionForm({
  isOpen,
  question,
  onOpenChange,
  onSubmit,
  isPending,
  t,
}: {
  isOpen: boolean;
  question: Question | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: QuestionInput) => Promise<void>;
  isPending: boolean;
  t: TFunction;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(question));
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((previous) => ({ ...previous, [key]: value }));
  const input = toInput(draft);
  const close = () => onOpenChange(false);
  const editing = question !== null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isPending || !input) return;

    try {
      await onSubmit(input);
      close();
    } catch {
      // Already reported by the caller; keep the form open to retry.
    }
  };

  const text = (
    key: keyof Draft & string,
    label: string,
    options: { required?: boolean; maxLength?: number } = {},
  ) => (
    <TextInput
      className="min-w-0 flex-1"
      name={key}
      label={label}
      value={draft[key] as string}
      onChange={(ev) => set(key, ev.target.value as Draft[typeof key])}
      isRequired={options.required}
      maxLength={options.maxLength}
    />
  );

  const number = (
    key: "questionSeq" | "pageNumber" | "pageSeq",
    label: string,
  ) => (
    <TextInput
      className="min-w-0 flex-1"
      type="number"
      name={key}
      label={label}
      value={draft[key]}
      onChange={(ev) => set(key, ev.target.value)}
      isRequired={true}
    />
  );

  return (
    <Modal
      control="external"
      data-test-role="question-modal"
      isDismissable={!isPending}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      width="wide"
      header={
        <h2 className="mb-4 text-size-18 text-on-surface">
          {editing
            ? t("manage.questions.form.editTitle")
            : t("manage.questions.form.addTitle")}
        </h2>
      }
    >
      {/* `Modal` never caps a tall body, so the cap is an inline `style`. */}
      <div
        className="flex max-w-full flex-col overflow-y-auto"
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={{ maxHeight: "calc(100vh - 320px)" }}
      >
        <form onSubmit={submit} className="flex flex-col gap-12">
          <div className="flex gap-12">
            {text("questionCode", t("manage.questions.form.code"), {
              required: true,
            })}
            {number("questionSeq", t("manage.questions.form.sequence"))}
          </div>
          <TextArea
            name="question"
            label={t("manage.questions.form.question")}
            value={draft.question}
            onChange={(ev) => set("question", ev.target.value)}
            isRequired={true}
          />
          <div className="flex gap-12">
            {text("questionLevel", t("manage.questions.form.level"), {
              required: true,
            })}
            {text("questionType", t("manage.questions.form.type"), {
              required: true,
            })}
          </div>
          <div className="flex gap-12">
            {text("frequency", t("manage.questions.form.frequency"), {
              required: true,
              maxLength: 1,
            })}
            {text("answerType", t("manage.questions.form.answerType"), {
              required: true,
              maxLength: 1,
            })}
            {text(
              "validationTable",
              t("manage.questions.form.validationTable"),
            )}
          </div>
          <div className="flex gap-12">
            {number("pageNumber", t("manage.questions.form.pageNumber"))}
            {number("pageSeq", t("manage.questions.form.pageSeq"))}
          </div>
          <div className="flex gap-12">
            {text("printOnTraxInv", t("manage.questions.form.printOnTraxInv"), {
              maxLength: 1,
            })}
            {text(
              "parentReactiveFlag",
              t("manage.questions.form.parentReactiveFlag"),
              { maxLength: 1 },
            )}
          </div>
          <div className="flex flex-wrap gap-16">
            <Checkbox
              name="entryRequired"
              value="true"
              label={t("manage.questions.form.entryRequired")}
              isSelected={draft.entryRequired}
              onChange={(selected) => set("entryRequired", selected)}
            />
            <Checkbox
              name="sampleQuestion"
              value="true"
              label={t("manage.questions.form.sampleQuestion")}
              isSelected={draft.sampleQuestion}
              onChange={(selected) => set("sampleQuestion", selected)}
            />
            <Checkbox
              name="activeFlag"
              value="true"
              label={t("manage.questions.form.active")}
              isSelected={draft.activeFlag}
              onChange={(selected) => set("activeFlag", selected)}
            />
          </div>
          <div className="mt-4 flex justify-end gap-8">
            <Button
              kind="text-only"
              variant="avatar-surface"
              type="submit"
              text={
                isPending
                  ? t("manage.questions.form.saving")
                  : t("manage.questions.form.save")
              }
              isDisabled={isPending || !input}
            />
            <Button
              kind="text-only"
              type="button"
              variant="tertiary"
              text={t("manage.questions.form.cancel")}
              onPress={close}
              isDisabled={isPending}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
}
